<template>
  <div class="new-order__main">
    <div class="container">
      <!-- INLINE SEARCH -->
      <div v-if="searchOpen" class="inline-search">
        <div class="modal__wrapper">
          <div class="modal__header">
            <button class="icon-button icon-button--round" @click="closeSearch">
              <i class="ri-close-circle-fill"></i>
            </button>
            <div class="modal__title">
              <span v-if="searchType === 'pickup'">Choose Pickup</span>
              <span v-else-if="searchType === 'dropOff'">Choose Drop-off</span>
              <span v-else>Choose Billing</span>
            </div>
            <button class="button button--fill" :class="{'button--fill--grey': !searchSelected}" @click="confirmSearch">Done</button>
          </div>
          <div class="modal__main">
            <div class="modal-search__label">
              <div v-if="searchType === 'pickup'" class="icon-button icon-button--round icon-button--blue">
                <i class="ri-arrow-up-circle-line"></i>
              </div>
              <div v-if="searchType === 'dropOff'" class="icon-button icon-button--round icon-button--orange">
                <i class="ri-arrow-down-circle-line"></i>
              </div>
              <div v-if="searchType === 'billing'" class="icon-button icon-button--round icon-button--blue">
                <i class="ri-money-dollar-circle-line"></i>
              </div>
              <input
                ref="inlineSearchField"
                type="text"
                class="modal-search__input"
                placeholder="Start typing business name"
                @input="onSearchInput"
              />
            </div>
            <div v-if="searchCustomers.length" class="modal-search__list">
              <div
                v-for="customer of searchCustomers"
                :key="customer.id"
                class="modal-search__item"
                :class="{
                  'modal-search__item--active': searchSelected && searchSelected.id === customer.id,
                  'modal-search__item--disabled': customer.isOutsideOperativeArea
                }"
                @click="selectCustomer(customer)"
              >
                <div class="modal-search__item-icon">
                  <i class="ri-checkbox-circle-fill"></i>
                </div>
                <div class="modal-search__item-customer">
                  <div class="modal-search__item-name">
                    <span v-if="customer.fathersName">{{ customer.fathersName }}' </span>
                    <span>{{ customer.firstName }} </span>
                    <span>{{ customer.middleInitial }} </span>
                    <span>{{ customer.lastName }} </span>
                    <span v-if="customer.nickName">({{ customer.nickName }})</span>
                  </div>
                  <div v-if="customer.isOutsideOperativeArea" class="modal-search__item-orange">
                    <span>Sorry, this person is outside STS delivery area</span>
                  </div>
                  <div class="modal-search__item-name">
                    <span>{{ customer.businessName }}</span>
                  </div>
                  <div class="modal-search__item-address">
                    <span>Address: </span>
                    <span>{{ customer.address }} {{ customer.city }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- MAIN FORM -->
      <div v-show="!searchOpen" class="new-order__main-title">Choose Pickup & Dropoff</div>
      <div v-show="!searchOpen" class="new-order__part">
        <div class="new-order__customer-wrapper">
          <div class="icon-button icon-button--round icon-button--blue">
            <i class="ri-arrow-up-circle-line"></i>
          </div>
          <div class="new-order__customer-inner">
            <div class="new-order__customer-name">
              <div class="new-order__customer-subtitle">Pickup</div>
              <div class="new-order__customer-title" @click="openSearch('pickup')">
                <span v-if="pickup && pickup.businessName">{{ $dots(pickup.businessName, 20) }}</span>
                <span v-else class="new-order__customer-desc">Pickup destination</span>
              </div>
            </div>
            <button
              v-if="!customer.isOutsideOperativeArea"
              class="button button--fill button--fill--blue"
              :class="{'button--fill--grey': pickupBtn}"
              @click="chooseMe('pickup')"
            >
              Choose me
            </button>
          </div>
        </div>

        <div class="new-order__customer-wrapper">
          <div class="icon-button icon-button--round icon-button--orange">
            <i class="ri-arrow-down-circle-line"></i>
          </div>
          <div class="new-order__customer-inner">
            <div class="new-order__customer-name">
              <div class="new-order__customer-subtitle">Dropoff</div>
              <div class="new-order__customer-title" @click="openSearch('dropOff')">
                <span v-if="dropOff && dropOff.businessName">{{ $dots(dropOff.businessName, 20) }}</span>
                <span v-else class="new-order__customer-desc">Dropoff destination</span>
              </div>
            </div>
            <button
              v-if="!customer.isOutsideOperativeArea"
              class="button button--fill button--fill--blue"
              :class="{'button--fill--grey': dropOffBtn}"
              @click="chooseMe('dropOff')"
            >
              Choose me
            </button>
          </div>
        </div>

        <div class="new-order__customer-wrapper">
          <div class="icon-button icon-button--round icon-button--orange">
            <i class="ri-arrow-down-circle-line"></i>
          </div>
          <div class="new-order__customer-inner">
            <div class="new-order__customer-name">
              <div class="new-order__customer-subtitle">Billing</div>
              <div class="new-order__customer-title" @click="openSearch('billing')">
                <span v-if="billing && billing.businessName">{{ $dots(billing.businessName, 20) }}</span>
                <span v-else class="new-order__customer-desc">Billing destination</span>
              </div>
            </div>
            <button class="button button--fill button--fill--blue" @click="chooseMe('billing')">
              Choose me
            </button>
          </div>
        </div>
      </div>
      <div v-show="!searchOpen" class="new-order__part">
        <div class="new-order__customer-wrapper">
          <div class="new-order__customer-label">
            <i class="ri-calendar-fill"></i>
            <input :value="date" :min="minDate" type="date" class="new-order__customer-input" @change="changeDate" />
          </div>
        </div>
        <div class="new-order__date-day">{{ dayLabel }}</div>
        <div v-if="dayHint" class="new-order__date-hint">{{ dayHint }}</div>
      </div>
      <FilesUpload v-show="!searchOpen" />
    </div>
  </div>
</template>

<script>
import FilesUpload from './FilesUpload'
import {earliestRouteDate, normalizeRouteDate, routeDayLabel, routeDayHint} from '@/utils/routeDate'
import {mapGetters} from 'vuex'
import _ from 'lodash'

export default {
  name: 'PublicNewOrderCustomer',
  components: {FilesUpload},
  data() {
    return {
      minDate: earliestRouteDate().format('YYYY-MM-DD'),
      dropOffBtn: false,
      pickupBtn: false,
      searchOpen: false,
      searchType: null,
      searchSelected: null
    }
  },
  computed: {
    ...mapGetters({
      customer: 'payment/customers/customer',
      pickup: 'newOrder/pickup',
      dropOff: 'newOrder/dropOff',
      billing: 'newOrder/billing',
      customerID: 'payment/customers/idCustomer',
      searchCustomers: 'payment/customers/customers',
      note: 'newOrder/note',
      file: 'newOrder/file',
      date: 'newOrder/date'
    }),
    dayLabel() {
      return routeDayLabel(this.date)
    },
    dayHint() {
      return routeDayHint(this.date)
    }
  },
  watch: {
    pickup(cus) {
      this.customerID !== cus.id ? (this.dropOffBtn = false) : null
    },
    dropOff(cus) {
      this.customerID !== cus.id ? (this.pickupBtn = false) : null
    }
  },
  mounted() {
    this.$store.commit('newOrder/setDate', this.businessDay(this.minDate))
  },
  methods: {
    openSearch(type) {
      this.searchType = type
      this.searchSelected = null
      this.searchOpen = true
      this.$nextTick(() => {
        if (this.$refs.inlineSearchField) {
          this.$refs.inlineSearchField.focus()
        }
      })
    },
    closeSearch() {
      this.searchOpen = false
      this.$store.commit('payment/customers/reset')
    },
    selectCustomer(cus) {
      this.searchSelected = cus
    },
    confirmSearch() {
      if (!this.searchSelected) return
      if (this.searchType === 'pickup') this.$store.commit('newOrder/setPickup', this.searchSelected)
      else if (this.searchType === 'dropOff') this.$store.commit('newOrder/setDropOff', this.searchSelected)
      else if (this.searchType === 'billing') this.$store.commit('newOrder/setBilling', this.searchSelected)
      this.closeSearch()
    },
    onSearchInput: _.debounce(async function(e) {
      let filters
      if (this.searchType === 'billing') {
        filters = {
          'filters[generalSearch]': e.target.value,
          'filters[publicOrCompanyID]': process.env.VUE_APP_COMPANY_ID,
          'filters[withoutEnterprise]': true,
          'filters[withCellPhone]': true
        }
      } else {
        filters = {
          'filters[generalSearch]': e.target.value,
          'filters[publicOrCompanyID]': process.env.VUE_APP_COMPANY_ID
        }
      }
      this.$store.commit('payment/customers/reset')
      await this.$store.dispatch('payment/customers/fetch', filters)
    }, 500),
    async chooseMe(type) {
      const customer = this.$store.getters['payment/customers/customer']
      if (type === 'pickup' && !this.pickupBtn) {
        this.$store.commit('newOrder/setPickup', customer)
        this.dropOffBtn = true
        this.pickupBtn = false
      } else if (type === 'dropOff' && !this.dropOffBtn) {
        this.$store.commit('newOrder/setDropOff', customer)
        this.pickupBtn = true
        this.dropOffBtn = false
      } else if (type === 'billing') {
        this.$store.commit('newOrder/setBilling', customer)
      }
    },
    changeDate(e) {
      this.$store.commit('newOrder/setDate', this.businessDay(e.target.value))
    },
    // Earliest bookable day (9 PM cutoff, no weekends) — matches the API rule,
    // so the default is never a date the server would reject.
    businessDay(dateStr) {
      return normalizeRouteDate(dateStr).format('YYYY-MM-DD')
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'style';
@import './SearchCustomer/style';
.inline-search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #f0f0f0;
  z-index: 1010;
  overflow-y: auto;
}
</style>
